import { supabase } from './supabase';
import type { Alert, AlertSeverity } from '@/types/alerts';

interface GetAlertsOptions {
  tenantId: string;
  projectIds?: string[];
  severity?: AlertSeverity[];
  acknowledged?: boolean;
  limit?: number;
}

export async function getAlerts(opts: GetAlertsOptions): Promise<Alert[]> {
  let q = supabase
    .from('alerts')
    .select('*')
    .eq('tenant_id', opts.tenantId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false })
    .limit(opts.limit ?? 100);

  if (opts.projectIds && opts.projectIds.length > 0) {
    q = q.in('project_id', opts.projectIds);
  }
  if (opts.severity && opts.severity.length > 0) {
    q = q.in('severity', opts.severity);
  }
  if (opts.acknowledged === true) {
    q = q.not('acknowledged_at', 'is', null);
  } else if (opts.acknowledged === false) {
    q = q.is('acknowledged_at', null);
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as Alert[];
}

export async function acknowledgeAlert(alertId: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const { error } = await supabase
    .from('alerts')
    .update({
      acknowledged_at: new Date().toISOString(),
      acknowledged_by: userId,
    })
    .eq('id', alertId);
  if (error) throw new Error(error.message);
}

export async function acknowledgeAllForProject(
  projectId: string,
  tenantId: string,
): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const { error } = await supabase
    .from('alerts')
    .update({
      acknowledged_at: new Date().toISOString(),
      acknowledged_by: userId,
    })
    .eq('tenant_id', tenantId)
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .is('acknowledged_at', null);
  if (error) throw new Error(error.message);
}

export async function getUnacknowledgedCount(
  tenantId: string,
  projectIds?: string[],
): Promise<number> {
  let q = supabase
    .from('alerts')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('is_deleted', false)
    .is('acknowledged_at', null);
  if (projectIds && projectIds.length > 0) {
    q = q.in('project_id', projectIds);
  }
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export interface UnacknowledgedSummary {
  total: number;
  top_severity: AlertSeverity | null;
}

export async function getUnacknowledgedSummary(
  tenantId: string,
  projectIds?: string[],
): Promise<UnacknowledgedSummary> {
  let q = supabase
    .from('alerts')
    .select('severity')
    .eq('tenant_id', tenantId)
    .eq('is_deleted', false)
    .is('acknowledged_at', null);
  if (projectIds && projectIds.length > 0) {
    q = q.in('project_id', projectIds);
  }
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ severity: AlertSeverity }>;
  if (rows.length === 0) return { total: 0, top_severity: null };

  const order: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
  let top: AlertSeverity = 'low';
  for (const sev of order) {
    if (rows.some((r) => r.severity === sev)) { top = sev; break; }
  }
  return { total: rows.length, top_severity: top };
}

export interface ActiveAlertsBreakdown {
  bySeverity: Record<AlertSeverity, number>;
  total: number;
  projectsAtRisk: { projectId: string; topSeverity: AlertSeverity; count: number }[];
  recentUnacknowledged: Alert[];
}

const EMPTY_ALERT_SEVERITY: Record<AlertSeverity, number> = {
  critical: 0, high: 0, medium: 0, low: 0,
};

const SEVERITY_PRIORITY: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];

/**
 * Tenant-wide (or scoped) summary of unacknowledged alerts. Used by the
 * Safety Manager "Active Alerts" widget and the Supervisor "Active
 * Alerts" widget.
 *
 * Returns: severity breakdown, total, projects with at least one
 * critical or high alert (with their top severity and alert count),
 * and the N most recently created unacknowledged alerts.
 *
 * Project name is not joined here — the widget should resolve names
 * from a separate project lookup it already has.
 */
export async function getActiveAlertsBreakdown(
  tenantId: string,
  opts?: { projectIds?: string[]; recentLimit?: number },
): Promise<ActiveAlertsBreakdown> {
  const recentLimit = opts?.recentLimit ?? 5;
  let q = supabase
    .from('alerts')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('is_deleted', false)
    .is('acknowledged_at', null)
    .order('created_at', { ascending: false });
  if (opts?.projectIds && opts.projectIds.length > 0) {
    q = q.in('project_id', opts.projectIds);
  }
  const { data, error } = await q;
  if (error) throw new Error(error.message);

  const rows = (data ?? []) as Alert[];
  const bySeverity: Record<AlertSeverity, number> = { ...EMPTY_ALERT_SEVERITY };
  const byProject = new Map<string, { topSeverity: AlertSeverity; count: number }>();
  for (const r of rows) {
    bySeverity[r.severity] = (bySeverity[r.severity] ?? 0) + 1;
    if (r.severity === 'critical' || r.severity === 'high') {
      const cur = byProject.get(r.project_id);
      if (!cur) {
        byProject.set(r.project_id, { topSeverity: r.severity, count: 1 });
      } else {
        cur.count += 1;
        if (
          SEVERITY_PRIORITY.indexOf(r.severity)
          < SEVERITY_PRIORITY.indexOf(cur.topSeverity)
        ) {
          cur.topSeverity = r.severity;
        }
      }
    }
  }

  const projectsAtRisk = Array.from(byProject.entries())
    .map(([projectId, v]) => ({ projectId, topSeverity: v.topSeverity, count: v.count }))
    .sort((a, b) => {
      const sevDiff =
        SEVERITY_PRIORITY.indexOf(a.topSeverity) - SEVERITY_PRIORITY.indexOf(b.topSeverity);
      return sevDiff !== 0 ? sevDiff : b.count - a.count;
    });

  return {
    bySeverity,
    total: rows.length,
    projectsAtRisk,
    recentUnacknowledged: rows.slice(0, recentLimit),
  };
}

export async function triggerAlertEngine(params: {
  projectId?: string;
  tenantId?: string;
  dsrId?: string;
  triggeredBy: 'dsr_submit' | 'schedule' | 'manual';
}): Promise<void> {
  try {
    await supabase.functions.invoke('alert-engine', { body: params });
  } catch (err) {
    // Fire-and-forget — never block the caller.
    // eslint-disable-next-line no-console
    console.warn('[alerts] engine invoke failed', err);
  }
}
